import { collection, endAt, limit, onSnapshot, orderBy, query, startAfter } from "firebase/firestore";

export const MESSAGE_PAGE_SIZE = 50;
export type MessagePageState = { messages: any[]; more: boolean; loading: boolean; error: boolean };

// Each loaded page becomes a fixed cursor range before the next is opened.
// New messages cannot push an older message into a gap between live pages.
// Only the newest, unopened tail uses limit(); historical status changes and
// deletions remain live as well. Timestamp ties are resolved by document ID.
export function listenMessagePages(db: any, threadId: string, emit: (s: MessagePageState) => void) {
  const base = collection(db, "whatsappThreads", threadId, "messages");
  type Page = { rows: any[]; cursor: any; start: any; pinned: boolean; ready: boolean;
    more: boolean; error: boolean; version: number; unsub?: () => void };
  const pages: Page[] = [];
  let active = true;
  const publish = () => {
    if (!active) return;
    const last = pages[pages.length - 1];
    const byId = new Map();
    for (const page of pages) for (const d of page.rows) byId.set(d.id, { id: d.id, ...d.data() });
    // Page queries already use a stable at DESC / __name__ DESC order.
    emit({ messages: Array.from(byId.values()).reverse(), more: !!last?.more,
      loading: pages.some((p) => !p.ready && !p.error), error: pages.some((p) => p.error) });
  };
  const listen = (p: Page) => {
    p.unsub?.();
    const version = ++p.version;
    const constraints: any[] = [orderBy("at", "desc")];
    if (p.start) constraints.push(startAfter(p.start));
    constraints.push(p.pinned ? endAt(p.cursor) : limit(MESSAGE_PAGE_SIZE));
    p.error = false;
    p.unsub = onSnapshot(query(base, ...constraints), { includeMetadataChanges: true }, (snap) => {
      if (!active || p.version !== version) return;
      const server = !snap.metadata.fromCache && !snap.metadata.hasPendingWrites;
      if (!p.pinned && server && p.cursor && snap.size === MESSAGE_PAGE_SIZE &&
          !snap.docs.some((d) => d.id === p.cursor.id)) {
        // The oldest visible message has been pushed out. Preserve its range
        // before publishing, so a person reading above never loses the row.
        p.pinned = true;
        listen(p);
        return;
      }
      p.rows = snap.docs;
      if (server) {
        p.ready = true;
        if (!p.pinned) {
          p.cursor = snap.docs[snap.docs.length - 1] || null;
          p.more = snap.size === MESSAGE_PAGE_SIZE;
        }
      }
      publish();
    }, () => { if (active && p.version === version) { p.error = true; publish(); } });
  };
  const add = (start: any) => {
    const p: Page = { rows: [], start, cursor: null, pinned: false, ready: false, more: false, error: false, version: 0 };
    pages.push(p);
    listen(p);
    publish();
  };
  add(null);
  return {
    more() {
      const p = pages[pages.length - 1];
      if (!active || pages.some((x) => !x.ready || x.error) || !p.more || !p.cursor) return;
      if (!p.pinned) { p.pinned = true; listen(p); }
      add(p.cursor);
    },
    retry() { for (const p of pages) if (p.error) listen(p); publish(); },
    close() { active = false; for (const p of pages) p.unsub?.(); },
  };
}
